<template>
  <Toast />

  <ConfirmPopup></ConfirmPopup>
  <div class="subscriber-view">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="title">Discover Data Products</h1>
        </div>
      </div>
    </div>

    <!-- Subscription Section -->
    <section class="subscription-section">
      <div class="subscription-card">
        <div class="card-header">
          <div class="header-content">
            <p class="section-description">
              Enter catalog endpoints and participant DID to discover and
              subscribe to available data products
            </p>
          </div>
        </div>

        <!-- Endpoint and DID Input -->
        <div class="input-section">
          <div class="input-group">
            <div class="input-row">
              <div class="input-container">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <path
                    d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
                <input
                  v-model="newEndpoint"
                  type="url"
                  placeholder="https://example.com/data-catalog"
                  class="endpoint-input"
                  @keyup.enter="addEndpoint"
                />
              </div>
              <div class="input-container">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 15l-3-3m0 0l3-3m-3 3h6m3 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
                <input
                  v-model="newParticipantDid"
                  type="text"
                  placeholder="did:example:123456789"
                  class="endpoint-input"
                />
              </div>
              <div class="input-container">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 15l-3-3m0 0l3-3m-3 3h6m3 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
                <input
                  v-model="catalogName"
                  type="text"
                  placeholder="Catalog Name"
                  class="endpoint-input"
                />
              </div>
              <button
                class="add-btn"
                @click="addEndpoint"
                :disabled="
                  !newEndpoint.trim() ||
                  !newParticipantDid.trim() ||
                  !catalogName
                "
              >
                <svg class="btn-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
                Add Subscription
              </button>
            </div>
            <p class="input-hint">
              Enter the full URL of the data catalog and the participant DID you
              want to subscribe to
            </p>
          </div>
        </div>

        <!-- Active Subscriptions -->
        <div
          v-if="subscribedEndpoints.length > 0"
          class="subscriptions-section"
        >
          <h3 class="subscriptions-title">Your Subscriptions</h3>
          <div>
            <div>
              <div
                v-for="(catalog, index) in subscribedEndpoints"
                :key="index"
                class="subscription-content subscription-item"
              >
                <svg class="subscription-icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
                <div class="subscription-details">
                  <span class="endpoint-url">{{ catalog.catalog_name }}</span>
                  <span class="participant-did">{{ catalog.endpoint }}</span>
                  <span class="participant-did"
                    >DID: {{ catalog.participant_did }}</span
                  >
                  <span class="subscription-status">Active</span>
                </div>
                <button
                  class="unsubscribe-btn"
                  @click="removeEndpoint($event, index)"
                  title="Unsubscribe from this endpoint"
                >
                  <svg class="unsubscribe-icon" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Subscriptions State -->
        <div v-else class="empty-subscriptions">
          <div class="empty-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </div>
          <h3 class="empty-title">No active subscriptions</h3>
          <p class="empty-description">
            Add a catalog endpoint and participant DID above to start
            discovering data assets
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed } from "vue";
import axios from "axios";
const { appContext } = getCurrentInstance();
const API_BASE_URL = appContext.config.globalProperties.$apiBase;
import { useConfirm } from "primevue/useconfirm";
import ConfirmPopup from "primevue/confirmpopup";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

const confirm = useConfirm();
//import { API_BASE_URL } from "../config";

const props = defineProps({
  assets: {
    type: Array,
    required: true,
  },
  user: {
    type: String,
    required: true,
  },

  contractInfo: {
    type: Array,
    required: false,
  },
});

const url = computed(() => props.contractInfo[0]?.url);
const did = computed(() => props.contractInfo[0]?.participantId);
const emit = defineEmits(["update-assets"]);
const toast = useToast();

// Refs
const newEndpoint = ref("");
const newParticipantDid = ref("");
const catalogName = ref("");
const subscribedEndpoints = ref([]);

// Methods
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

const isValidDid = (did) => {
  // Basic DID validation - you can enhance this based on your DID format requirements
  return did && did.trim().length > 0 && did.startsWith("did:");
};

const addEndpoint = async () => {
  if (
    !newEndpoint.value.trim() ||
    !newParticipantDid.value.trim() ||
    !catalogName.value.trim()
  )
    return;

  if (!isValidUrl(newEndpoint.value)) {
    toast.add({
      severity: "error",
      summary: "Subscription Error",
      detail: "Please enter a valid URL starting with http:// or https://",
      life: 2500,
    });
    return;
  }

  if (!isValidDid(newParticipantDid.value)) {
    alert("Please enter a valid DID (should start with 'did:')");
    return;
  }
  // Check if combination already exists
  if (
    subscribedEndpoints.value.some(
      (sub) =>
        sub.endpoint === newEndpoint.value &&
        sub.participant_did === newParticipantDid.value
    )
  ) {
    toast.add({
      severity: "error",
      summary: "Subscription Error",
      detail: "This endpoint already subscribed",
      life: 2500,
    });
    return;
  }

  if (
    newEndpoint.value == url.value.split("/api")[0] &&
    newParticipantDid.value == did.value
  ) {
    toast.add({
      severity: "error",
      summary: "Subscription Error",
      detail: "Can't subscribe to your own connector",
      life: 2500,
    });
    return;
  }

  console.log("Adding new subscription with endpoint:", props.user);

  try {
    await axios.post(`${API_BASE_URL}/users/${props.user}/endpoints`, {
      endpoint: newEndpoint.value,
      participant_did: newParticipantDid.value,
      catalog_name: catalogName.value,
    });
    await fetchEndpoints(props.user);
    toast.add({
      severity: "success",
      summary: "Subscription Added",
      life: 2500,
    });
    newEndpoint.value = "";
    newParticipantDid.value = "";
    catalogName.value = "";
  } catch (error) {
    console.error("Error adding endpoint:", error);
    toast.add({
      severity: "error",
      summary: "Subscription Error",
      detail: `Error adding endpoint:, ${error}`,
      life: 2500,
    });
  }
};

const fetchEndpoints = async (user) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/users/${user}/endpoints`);
    subscribedEndpoints.value = res.data;
    console.log("Fetched subscribed endpoints:", subscribedEndpoints.value[0]);
  } catch (error) {
    console.error("Error fetching endpoints:", error);
  }
};

const removeEndpoint = async (event, index) => {
  console.log("Attempting to remove subscription at index:", index);

  const subscription = subscribedEndpoints.value[index];
  console.log("Removing subscription:", subscription);

  confirm.require({
    target: event.currentTarget,
    message: "Do you want to delete this record?",
    icon: "pi pi-info-circle",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: async () => {
      console.log("Delete clicked");
      const response = await axios.post(
        `${API_BASE_URL}/repository/find/prop/${props.user}`,
        { objectType: { name: "User" } },
        {
          params: {
            includeContent: "true",
            pageNumber: 1,
            pageSize: 200,
            sortBy: "_id",
          },
        }
      );
      console.log(response.data);
      const userRecord = response.data.find(
        (record) => record.name === props.user
      );
      userRecord.contentData.subscriptions =
        userRecord.contentData.subscriptions.filter((entry) => {
          return !(
            entry.endpoint === subscription.endpoint &&
            entry.catalog_name === subscription.catalog_name
          );
        });
      const subRes = await axios.post(
        `${API_BASE_URL}/repository/update/full`,
        userRecord
      );
      if (subRes.data.acknowledged) {
        fetchEndpoints(props.user);
        toast.add({
          severity: "warn",
          summary: "Subscription Removed",
          detail: `Subscription removed`,
          life: 2500,
        });
      }
    },
  });
};

// Lifecycle
onMounted(() => {
  fetchEndpoints(props.user);
});
</script>

<style scoped>
.subscriber-view {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #2d3748;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #009688 0%, #00695c 100%);
  border-radius: 16px;
  padding: 2.5rem;
  margin-bottom: 3rem;
  color: white;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 2rem;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.title {
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.catalog-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Subscription Section */
.subscription-section {
  margin-bottom: 3rem;
}

.subscription-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.card-header {
  margin-bottom: 2rem;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.section-description {
  font-size: 1rem;
  color: black;
  margin: 0;
  line-height: 1.6;
}

/* Input Section */
.input-section {
  margin-bottom: 2.5rem;
}

.input-group {
  max-width: 800px;
}

.input-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.75rem;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.5rem;
  transition: all 0.3s ease;
}

.input-container:focus-within {
  border-color: #009688;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-icon {
  width: 20px;
  height: 20px;
  color: #a0aec0;
  margin: 0 0.75rem;
  flex-shrink: 0;
}

.endpoint-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.75rem 0;
  font-size: 1rem;
  color: #2d3748;
  outline: none;
}

.endpoint-input::placeholder {
  color: #a0aec0;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #009688;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: fit-content;
}

.add-btn:hover:not(:disabled) {
  background: #009688;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.add-btn:disabled {
  background: #cbd5e0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.input-hint {
  font-size: 0.85rem;
  color: #718096;
  margin: 0;
}

/* Subscriptions Section */
.subscriptions-section {
  border-top: 1px solid #e2e8f0;
  padding-top: 2rem;
}

.subscriptions-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1.5rem 0;
}

.subscriptions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.subscription-item {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  transition: all 0.2s ease;
}

.subscription-item:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

.subscription-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.subscription-icon {
  width: 20px;
  height: 20px;
  color: #48bb78;
  flex-shrink: 0;
}

.subscription-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.endpoint-url {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.95rem;
}

.participant-did {
  font-size: 0.85rem;
  color: #718096;
  font-family: monospace;
}

.subscription-status {
  font-size: 0.8rem;
  color: #48bb78;
  font-weight: 600;
  margin-top: 0.25rem;
}

.unsubscribe-btn {
  background: #fed7d7;
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  color: #e53e3e;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 1rem;
}

.unsubscribe-btn:hover {
  background: #feb2b2;
  transform: scale(1.05);
}

.unsubscribe-icon {
  width: 16px;
  height: 16px;
}

/* Empty Subscriptions */
.empty-subscriptions {
  text-align: center;
  padding: 3rem 2rem;
  border-top: 1px solid #e2e8f0;
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: #f7fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
}

.empty-icon svg {
  width: 32px;
  height: 32px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  color: #718096;
  margin: 0;
  font-size: 0.95rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .subscriber-view {
    padding: 1rem;
  }

  .title {
    font-size: 2rem;
  }

  .subscription-card {
    padding: 1.5rem;
  }

  .input-row {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .input-container {
    flex-direction: row;
    padding: 0.75rem;
  }

  .add-btn {
    justify-content: center;
    margin-top: 0.5rem;
  }

  .subscription-item {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .subscription-content {
    gap: 0.75rem;
  }

  .unsubscribe-btn {
    margin-left: 0;
    align-self: flex-end;
  }
}

@media (max-width: 480px) {
  .title {
    font-size: 1.75rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .subscription-card {
    padding: 1.25rem;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.subscription-item {
  animation: fadeIn 0.3s ease forwards;
}

.subscription-item:nth-child(1) {
  animation-delay: 0.1s;
}
.subscription-item:nth-child(2) {
  animation-delay: 0.2s;
}
.subscription-item:nth-child(3) {
  animation-delay: 0.3s;
}
</style>
